import { t } from "@lingui/core/macro";
import { WAITING_ON_USER_RUN_STATUSES, WORKING_RUN_STATUSES } from "@rakazo/core";

export function rosterHasUndismissedRunError(
  runErrorId: string | null | undefined,
  dismissedRunErrorIds: ReadonlySet<string>,
): boolean {
  return Boolean(runErrorId && !dismissedRunErrorIds.has(runErrorId));
}

/**
 * Short sidebar label while a bot is queued, starting, or running. Null keeps the message preview.
 * The roster reports a queued run only when no other run of that bot is ahead of it (a running or
 * waiting one wins), so a queued run is about to start rather than waiting its turn.
 */
export function rosterWorkStatusLabel(status: string): string | null {
  switch (status) {
    case "queued":
    case "leased":
      return t`Starting…`;
    case "running":
      return t`Working…`;
    default:
      return null;
  }
}

/** Compact rows keep actionable status visible in place of the message preview. */
export function compactRosterStatusLabel(status: string): string | null {
  const workStatus = rosterWorkStatusLabel(status);
  if (workStatus) return workStatus;
  switch (status) {
    case "waiting_input":
      return t`Needs an answer`;
    case "waiting_takeover":
      return t`Needs you`;
    case "failed":
      return t`Error`;
    default:
      return null;
  }
}

export type ChatHeaderState = "idle" | "working" | "needs-you";

/** The state under a bot's name in the chat header. */
export function chatHeaderState(status: string): ChatHeaderState {
  if ((WORKING_RUN_STATUSES as readonly string[]).includes(status)) return "working";
  if ((WAITING_ON_USER_RUN_STATUSES as readonly string[]).includes(status)) return "needs-you";
  return "idle";
}
